/**
 * 北京时间格式化工具
 * 系统所有时间统一使用北京时间（UTC+8）
 */

const BJ_TZ = "Asia/Shanghai";

/** 将任意时间值转为Date对象 */
export function toDate(d: Date | string | number): Date {
  if (d instanceof Date) return d;
  if (typeof d === "string") {
    // [Bug fix 2026-04-30] 优先识别已带时区标记的字符串(Z 或 +/-XX:XX)
    // trpc + superjson 反序列化后通常已是 Date 对象,但若字符串经过链路也能正确处理
    if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(d)) {
      return new Date(d);
    }
    // MySQL 直返字符串(无时区标记)：YYYY-MM-DD HH:MM:SS 或 YYYY-MM-DDTHH:MM:SS(.毫秒)?
    // 这种格式表示北京时间,加 +08:00 标记避免被当成 UTC
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(d)) {
      return new Date(d.replace(" ", "T") + "+08:00");
    }
    return new Date(d);
  }
  return new Date(d);
}

/** 格式化为北京时间 */
export function formatBJ(
  d: Date | string | number,
  fmt: "time" | "datetime" | "short" | "date" | "dateShort" | "monthDay" | "dateTime" = "datetime"
): string {
  const date = toDate(d);
  switch (fmt) {
    case "time":
      return date.toLocaleTimeString("zh-CN", { timeZone: BJ_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit" });
    case "dateTime":
      // MM/DD HH:mm:ss 格式，适合日志展示
      return date.toLocaleString("zh-CN", { timeZone: BJ_TZ, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    case "short":
      return date.toLocaleTimeString("zh-CN", { timeZone: BJ_TZ, hour: "2-digit", minute: "2-digit" });
    case "date":
      return date.toLocaleDateString("zh-CN", { timeZone: BJ_TZ, year: "numeric", month: "2-digit", day: "2-digit" });
    case "dateShort":
      return date.toLocaleDateString("zh-CN", { timeZone: BJ_TZ, month: "2-digit", day: "2-digit" });
    case "monthDay":
      return date.toLocaleDateString("zh-CN", { timeZone: BJ_TZ, month: "numeric", day: "numeric" });
    case "datetime":
    default:
      return date.toLocaleString("zh-CN", { timeZone: BJ_TZ });
  }
}

/** 获取北京时间的小时数 (0-23) */
export function getBJHour(d: Date | string | number): number {
  return parseInt(toDate(d).toLocaleString("en-US", { timeZone: BJ_TZ, hour: "numeric", hour12: false }));
}

/** 获取北京时间的分钟数 (0-59) */
export function getBJMinute(d: Date | string | number): number {
  return parseInt(toDate(d).toLocaleString("en-US", { timeZone: BJ_TZ, minute: "numeric" }));
}

/** 格式化为北京时间的 HH:mm */
export function formatBJHM(d: Date | string | number): string {
  const h = getBJHour(d).toString().padStart(2, "0");
  const m = getBJMinute(d).toString().padStart(2, "0");
  return `${h}:${m}`;
}

/** 格式化为相对时间（刚刚 / x分钟前 / x小时前 / 日期） */
export function formatRelative(d: Date | string | number): string {
  const now = Date.now();
  const ts = toDate(d).getTime();
  const diff = now - ts;
  if (diff < 60_000) return "刚刚";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}分钟前`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}小时前`;
  return formatBJ(d, "dateShort");
}

/**
 * 判断A股交易时段（北京时间）
 * 交易时段: 9:15-11:30, 13:00-15:00（周一到周五）
 * 返回: { isTradingHours: boolean, isWeekday: boolean }
 */
export function checkTradingHours(now?: Date): { isTradingHours: boolean; isWeekday: boolean } {
  const d = now || new Date();
  // 获取北京时间的星期几 (0=周日, 6=周六)
  const bjDayStr = d.toLocaleString("en-US", { timeZone: BJ_TZ, weekday: "short" });
  const isWeekday = !["Sun", "Sat"].includes(bjDayStr);
  
  if (!isWeekday) return { isTradingHours: false, isWeekday: false };
  
  const h = getBJHour(d);
  const m = getBJMinute(d);
  const timeMinutes = h * 60 + m; // 转为分钟数方便比较
  
  // 9:15-11:30 (555-690) 或 13:00-15:00 (780-900)
  const isTradingHours = (timeMinutes >= 555 && timeMinutes <= 690) || (timeMinutes >= 780 && timeMinutes <= 900);
  
  return { isTradingHours, isWeekday };
}

/**
 * 智能轮询间隔：根据交易时段返回不同的轮询间隔
 * @param tradingMs 交易时段的轮询间隔（毫秒）
 * @param idleMs 非交易时段的轮询间隔（毫秒），默认为交易时段的 6 倍
 * @param isTradingHours 是否在交易时段（可从 engineStatus 获取，或用 checkTradingHours 判断）
 */
export function smartInterval(tradingMs: number, isTradingHours: boolean, idleMs?: number): number {
  if (isTradingHours) return tradingMs;
  return idleMs ?? Math.min(tradingMs * 6, 60000); // 非交易时段最多60秒
}

/**
 * AI信号专用轮询间隔（北京时间）
 * - 08:25-08:45：5秒（开盘前准备）
 * - 23:00-07:00：false（停止轮询）
 * - 其余时间：30秒
 */
export function aiSignalInterval(now?: Date): number | false {
  const d = now || new Date();
  const bjDayStr = d.toLocaleString("en-US", { timeZone: "Asia/Shanghai", weekday: "short" });
  const isWeekday = !["Sun", "Sat"].includes(bjDayStr);
  const h = getBJHour(d);
  const m = getBJMinute(d);
  const t = h * 60 + m; // 总分钟

  // 23:00-07:00 不轮询
  if (t >= 23 * 60 || t < 7 * 60) return false;

  // 08:25-08:45 5秒轮询（开盘前窗口）
  if (t >= 8 * 60 + 25 && t <= 8 * 60 + 45) return 5000;

  // 其余时间 30秒
  return 30000;
}
